import type { JSX, KeyboardEvent } from 'react';
import { useState } from 'react';

import { Check, X } from 'lucide-react';

import { Button } from '../Button/Button';
import { Input } from '../Inputs/Input';

import styles from './RichTextEditor.module.css';

interface InsertImageBarProps {
  onInsert: (src: string, alt: string) => void;
  onClose: () => void;
}

/**
 * Only `https` is accepted, and that is not a shortcut.
 *
 * These editors write email. An image in an outbound message has to be
 * fetchable by the recipient's mail client from wherever they open it, so a
 * hosted URL is the only form that actually works — a `data:` payload is
 * stripped by the server's sanitizer (and blocked or flagged as spam by many
 * clients), and a presigned upload URL would expire and leave a broken image
 * in mail already sent. Rejecting those here is what stops one being pasted,
 * looking fine in the editor, and silently vanishing on save.
 *
 * Plain `http` is excluded for the same reason rather than a purist one: mail
 * clients and image proxies routinely refuse mixed-content images, so an
 * `http://` logo renders in the editor and then breaks in the message.
 */
const HTTPS_URL = /^https:\/\/\S+$/i;

/**
 * Inline "insert an image" bar.
 *
 * An in-flow bar rather than a floating popover, for the same reason as the
 * link bar next to it: the editors live inside scroll containers (modal body,
 * slide-out panel) that would clip a popover.
 */
export function InsertImageBar({ onInsert, onClose }: InsertImageBarProps): JSX.Element {
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const apply = (): void => {
    const url = src.trim();
    if (!HTTPS_URL.test(url)) {
      setError(
        url.toLowerCase().startsWith('data:')
          ? 'Paste a hosted image URL. Embedded image data is not kept — mail clients need a URL they can fetch.'
          : 'Enter an image URL starting with https:// — mail clients often refuse anything else.',
      );
      return;
    }
    onInsert(url, alt.trim());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      apply();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div className={styles.ImageBar}>
      <div className={styles.LinkBar}>
        <Input
          type="url"
          value={src}
          onChange={(event) => {
            setSrc(event.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder="https://example.com/logo.png"
          aria-label="Image URL"
          autoFocus
          className={styles.LinkInput}
        />
        <Input
          type="text"
          value={alt}
          onChange={(event) => setAlt(event.target.value)}
          onKeyDown={handleKeyDown}
          // Many mail clients block remote images by default and show this
          // text instead, so it is doing real work, not just a11y box-ticking.
          placeholder="Description (shown when images are blocked)"
          aria-label="Image description"
          className={styles.LinkInput}
        />
        <Button variant="primary" onClick={apply} className={styles.LinkAction}>
          <Check aria-hidden="true" className={styles.LinkActionIcon} />
          Insert
        </Button>
        <button type="button" className={styles.LinkClose} aria-label="Cancel" title="Cancel" onClick={onClose}>
          <X aria-hidden="true" />
        </button>
      </div>
      {error && <p className={styles.BarError}>{error}</p>}
    </div>
  );
}

export default InsertImageBar;
